import { ref, computed } from 'vue';
import { useRuntimeConfig, useState } from '#app';

// Types for clarity
type Permission = {
  id: number | string;
  name: string;
  display_name: string;
  description: string;
  created_at: string;
  updated_at: string;
  pivot: {
    role_id: number | string;
    permission_id: number | string;
  }
};

type Role = {
  id: number | string;
  name: string;
  display_name: string;
  description: string;
  created_at: string;
  updated_at: string;
  ad_group: any[];
  permissions: Permission[];
  pivot: {
    user_type: string;
    user_id: number | string;
    role_id: number | string;
  }
};

type User = {
  id: number | string;
  username: string;
  first_name: string;
  last_name: string;
  display_name: string;
  email: string;
  description: string;
  active: boolean;
  sis_id: number | string | null;
  fiscal_id: number | string | null;
  picture: string;
  roles: Role[];
  permissions: Permission[];
  // extend with your API's fields
};

type TokenResponse = {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
};

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
};

// 🔑 single source of truth for the storage key
const LOCAL_STORAGE_KEY = 'mylhs-auth';

// Single source of truth for auth state inside Nuxt
function useAuthState() {
  const state = useState<AuthState>('mylhs-auth-state', () => ({
    accessToken: null,
    refreshToken: null,
    expiresAt: null,
  }));

  return state;
}

export function useAuth() {
  const config = useRuntimeConfig();

  const apiBase = config.public.apiBase;
  const clientId = config.public.authClientId;
  const clientSecret = config.public.authClientSecret;

  const authState = useAuthState();
  const user = useState<User | null>('mylhs-auth-user', () => null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!authState.value.accessToken);

  // Persist to localStorage on client (Nuxt 4 SPA)
  const syncToStorage = () => {
    if (import.meta.client) {
      const payload = JSON.stringify(authState.value);
      localStorage.setItem(LOCAL_STORAGE_KEY, payload);
    }
  };

  const loadFromStorage = () => {
    if (import.meta.client) {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw) as AuthState;
        authState.value = parsed;
      } catch {
        // corrupted storage, clear it
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }
  };

  // Call this once on app mount / first usage on client
  const initAuth = async () => {
    if (import.meta.client) {
      loadFromStorage();
      // Optionally try to fetch user if we seem to have a token
      if (authState.value.accessToken && user.value === null) {
        await fetchUser().catch(() => {
          // token invalid, nuke state
          resetAuth();
          syncToStorage();
        });
      }
    }
  };

  const setTokens = (token: TokenResponse) => {
    const now = Date.now();

    authState.value.accessToken = token.access_token;
    authState.value.refreshToken = token.refresh_token ?? null;
    authState.value.expiresAt = now + token.expires_in * 1000;
    syncToStorage();
  };

  
  const getAccessToken = () => {
    return authState.value.accessToken;
  };

  const resetAuth = () => {
    authState.value.accessToken = null;
    authState.value.refreshToken = null;
    authState.value.expiresAt = null;
    user.value = null;
  };

  const ensureFreshToken = async () => {
    const { accessToken, refreshToken, expiresAt } = authState.value;

    if (!accessToken || !expiresAt) return;

    const now = Date.now();
    const skew = 30_000; // refresh 30 seconds early

    if (now + skew < expiresAt) return;
    if (!refreshToken) return;

    await refresh();
  };

  const loginWithPassword = async (username: string, password: string) => {
    loading.value = true;
    error.value = null;

    try {
      const token = await $fetch<TokenResponse>(`${apiBase}/oauth/token`, {
        method: 'POST',
        body: {
          grant_type: 'password',
          client_id: clientId,
          client_secret: clientSecret,
          username,
          password,
          scope: '*',
        },
      });

      setTokens(token);

      // fetch current user
      await fetchUser();

      return user.value;
    } catch (err: any) {
      // Laravel often returns { message, errors, ... }
      const msg =
        err?.data?.message ||
        err?.data?.error_description ||
        'Login failed';
      error.value = msg;
      resetAuth();
      syncToStorage();
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchUser = async () => {
    if (!authState.value.accessToken) {
      user.value = null;
      return null;
    }

    await ensureFreshToken();

    const { accessToken } = authState.value;

    const me = await $fetch<User>(`${apiBase}/api/v2/users/me`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    user.value = me;
    return user.value;
  };

  const refresh = async () => {
    const { refreshToken } = authState.value;
    if (!refreshToken) return;

    const token = await $fetch<TokenResponse>(`${apiBase}/oauth/token`, {
      method: 'POST',
      body: {
        grant_type: 'refresh_token',
        client_id: clientId,
        client_secret: clientSecret,
        refresh_token: refreshToken,
        scope: '',
      },
    });

    setTokens(token);
  };

  const logout = async () => {
    try {
      // Optional: hit a Laravel logout route that revokes tokens
      if (authState.value.accessToken) {
        await $fetch(`${apiBase}/api/v2/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authState.value.accessToken}`,
          },
        }).catch(() => {
          // don't hard-fail if backend doesn't implement it yet
        });
      }
    } finally {
      resetAuth();
      syncToStorage();
    }
  };

  /**
   * Socialite: start provider login.
   * This doesn’t do OAuth in the SPA – it just redirects to Laravel’s Socialite route.
   * Feel free to customize the route/params to your backend.
   */
  const loginWithProvider = (provider: string, redirectTo?: string) => {
    if (!import.meta.client) return;

    const dest =
      redirectTo ??
      `${window.location.origin}/auth/callback`; // SPA route you handle

    const url = new URL(`${apiBase}/auth/${provider}`); // /redirect
    url.searchParams.set('redirect_uri', dest);

    window.location.href = url.toString();
  };

  return {
    // state
    user,
    isAuthenticated,
    loading,
    error,
    authState,

    // lifecycle
    initAuth,

    // actions
    loginWithPassword,
    loginWithProvider,
    fetchUser,
    getAccessToken,
    refresh,
    ensureFreshToken,
    logout,
  };
}
